import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import dayjs from "dayjs";
import { useFormikContext } from "formik";

import {
  $Button,
  $DateLabel,
  $Popconfirm,
  $Skeleton,
  $Tag,
  $TableTree,
  $Tooltip,
} from "us.common/components";
import { EditOutlined, DeleteOutlined } from "us.icons";
import { NonProductionDay } from "us.collection.admin/interfaces";
import { RootState } from "us.helper/types";
import {
  NON_PROD_DAY_STATUS_COLORS,
  NON_PROD_DAY_STATUS_LABELS,
  NonProdDayStatus,
} from "us.collection.admin/constants";
import { getFilteredRedDays } from "../../functions";
import { handleNonProductionDaysTableFilter } from "us.collection.admin/functions/NonProductionDays/NonProductionDays";

type NonExecutionDayProps = PropsFromRedux & {
  onView: (record: NonProductionDay) => void;
  onDelete: (record: NonProductionDay) => void;
};

/**
 * @description Non Execution Day Component
 * @link Design document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3049914453/Non+Email+Days+UI+Design
 * @author Rajitha Sanjayamal <rajithasa@unicorn-solutions.com>
 * @since 21/07/2022
 */
const NonExecutionDays: React.FC<NonExecutionDayProps> = (props) => {
  const { t } = useTranslation();
  const { selectedCriteria, redDays, onDelete, onView, currentDateFormat } =
    props;
  const { values: redDayInfo } = useFormikContext<any>();

  /**
   * @function
   * @description get days by selected criteria
   */
  const filteredDays = useMemo(() => {
    return getFilteredRedDays(
      redDays.data,
      selectedCriteria,
      redDayInfo?.selectedYear,
    );
  }, [redDays.data, selectedCriteria, redDayInfo?.selectedYear]);

  // define table columns with custom renderers and filters
  const columns: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: "90px",
      customFilter: false,
      customRenderChild: (_text: any, record: any) => (
        <div className="d-flex">
          <$Tooltip placement="top" title={t("US.COMMON:COMMON.EDIT")}>
            <$Button
              icon={<EditOutlined />}
              size="small"
              className="mr-2"
              onClick={() => onView(record)}
              disabled={dayjs().isAfter(dayjs(record.endDate), "day")}
            />
          </$Tooltip>

          <$Popconfirm
            title={t(
              "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.POP_CONFIRMATION",
            )}
            onConfirm={() => onDelete(record)}
            placement="topLeft"
            disabled={dayjs().isAfter(dayjs(record.endDate), "day")}
          >
            <$Tooltip placement="top" title={t("US.COMMON:COMMON.DELETE")}>
              <$Button
                icon={<DeleteOutlined />}
                size="small"
                id="btn-edit-setting"
                className="text-error"
                disabled={dayjs().isAfter(dayjs(record.endDate), "day")}
              />
            </$Tooltip>
          </$Popconfirm>
        </div>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.FROM_DATE"),
      dataIndex: "startDate",
      key: "startDate",
      width: "140px",
      customFilter: "date",
      showFilters: true,
      customSorter: (a: any, b: any) =>
        new Date(a).getTime() - new Date(b).getTime(),
      customRenderChild: (text: any, record: any, index: any) => {
        return (
          <span>
            <$DateLabel value={record.startDate} />
          </span>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.TO_DATE"),
      dataIndex: "endDate",
      key: "endDate",
      width: "140px",
      customFilter: "date",
      showFilters: true,
      customSorter: (a: any, b: any) =>
        new Date(a).getTime() - new Date(b).getTime(),
      customRenderChild: (text: any, record: any, index: any) => {
        return (
          <span>
            <$DateLabel value={record.endDate} />
          </span>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.REASON"),
      dataIndex: "reason",
      key: "reason",
      ellipsis: { showTitle: false },
      customFilter: true,
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customRenderChild: (text: any, record: any, index: any) => {
        return <span>{record.reason}</span>;
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.ACTION"),
      dataIndex: "status",
      key: "action",
      width: "120px",
      customFilter: true,
      customSorter: (a: any, b: any) => a - b,
      customRenderChild: (_text: any, record: any) => {
        return (
          <$Tag
            color={
              NON_PROD_DAY_STATUS_COLORS[record.status as NonProdDayStatus]
            }
          >
            {t(NON_PROD_DAY_STATUS_LABELS[record.status as NonProdDayStatus])}
          </$Tag>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.RECURRING"),
      dataIndex: "isRecurring",
      key: "isRecurring",
      width: "120px",
      customFilter: true,
      customSorter: (a: any, b: any) => (a ? 1 : 0) - (b ? 1 : 0),
      customRenderChild: (_text: any, record: any) => {
        return (
          <span>{record?.isRecurring ? t("US.COMMON:COMMON.YES") : "-"}</span>
        );
      },
    },
    {
      title: t("US.COMMON:MANAGE_USER.MODIFIED_USER"),
      dataIndex: "modifiedBy",
      key: "modifiedBy",
      width: "360px",
      customFilter: true,
      ellipsis: { showTitle: false },
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customRenderChild: (text: any) => (
        <$Tooltip placement="topLeft" title={text} key={text}>
          {text || "-"}
        </$Tooltip>
      ),
    },
    {
      title: t("US.COLLECTION.ADMIN:EXTENDED_FIELDS.MODIFIED_DATE"),
      dataIndex: "modifiedDate",
      key: "modifiedDate",
      width: "160px",
      customSorter: (a: any, b: any) =>
        new Date(a).getTime() - new Date(b).getTime(),
      customFilter: "date",
      showFilters: true,
      customRenderChild: (text: any) => {
        return (
          <span>
            <$DateLabel value={text} />
          </span>
        );
      },
    },
  ];

  // memoized filter handler to prevent unnecessary re-renders of the table when filters change
  const handleFilter = useMemo(
    () => handleNonProductionDaysTableFilter({ t, currentDateFormat }),
    [currentDateFormat, t],
  );

  return (
    <div>
      <$Skeleton loading={redDays?.isFetching} active paragraph={{ rows: 2 }}>
        <$TableTree
          rowKey="id"
          className="mt-2"
          data={filteredDays}
          columns={columns}
          pagination={false}
          bordered
          size="small"
          scroll={{ y: "calc(100vh - 280px)" }}
          onRow={(record: any, rowIndex: any) => {
            return {
              onDoubleClick: (event: any) => {
                onView(record);
              },
            };
          }}
          onSort={(sortData, dataSource) => sortData(dataSource)}
          onFilter={handleFilter}
          filterOnType={true}
          resetOnSourceChange={true}
          virtual={true}
        />
      </$Skeleton>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { nonProductionDays, common } = state;
  const { redDays, selectedCriteria } = nonProductionDays;
  const { currentDateFormat } = common;
  return { redDays, selectedCriteria, currentDateFormat };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(NonExecutionDays);
